import { WalletFund } from 'src/types/wallet';

export type WalletFundTreeNode = WalletFund & {
  isFic?: boolean;
  composition?: WalletFundTreeNode[];
};

/**
 * Conta os fundos lógicos usados na composição. Filhos de um FIC pertencem à
 * composição do pai e, por isso, não devem somar uma composição adicional.
 */
export const countLogicalWalletFunds = (
  walletFunds: WalletFund[] | undefined,
): number => {
  if (!walletFunds || walletFunds.length === 0) {
    return 0;
  }

  return walletFunds.filter((walletFund) => !walletFund.parentWalletFund)
    .length;
};

/**
 * Conta todos os fundos persistidos e monitorados na carteira, incluindo
 * filhos de FIC.
 */
export const countMonitoredWalletFunds = (
  walletFunds: WalletFund[] | undefined,
): number => walletFunds?.length ?? 0;

/**
 * Conta a árvore de fundos seguidos uma única vez. A resposta pode trazer os
 * filhos achatados, aninhados em `childrenWalletFunds` ou em `composition`.
 * Os IDs visitados evitam duplicar fundos presentes nas duas representações.
 */
export const getFollowedFundHierarchyCounts = (
  walletFunds: WalletFundTreeNode[] | undefined,
): { rootFicParentsCount: number; totalFundsCount: number } => {
  if (!walletFunds?.length) {
    return { rootFicParentsCount: 0, totalFundsCount: 0 };
  }

  const fundsById = new Map<number, WalletFundTreeNode>();
  const parentByFundId = new Map<number, number | undefined>();
  const ficParentsWithChildren = new Set<number>();

  const visit = (walletFund: WalletFundTreeNode, inferredParentId?: number) => {
    if (fundsById.has(walletFund.id)) return;

    fundsById.set(walletFund.id, walletFund);
    parentByFundId.set(
      walletFund.id,
      walletFund.parentWalletFund?.id ?? inferredParentId,
    );

    const children = [
      ...(walletFund.childrenWalletFunds ?? []),
      ...(walletFund.composition ?? []),
    ] as WalletFundTreeNode[];

    if (children.length > 0) {
      ficParentsWithChildren.add(walletFund.id);
    }

    children.forEach((child) => visit(child, walletFund.id));
  };

  walletFunds.forEach((walletFund) => visit(walletFund));

  parentByFundId.forEach((parentId) => {
    if (parentId !== undefined) {
      ficParentsWithChildren.add(parentId);
    }
  });

  const rootFicParentsCount = Array.from(fundsById.values()).filter(
    (walletFund) => {
      const parentId = parentByFundId.get(walletFund.id);
      const isRoot = parentId === undefined || !fundsById.has(parentId);

      return (
        isRoot &&
        (walletFund.isFic === true || ficParentsWithChildren.has(walletFund.id))
      );
    },
  ).length;

  return {
    rootFicParentsCount,
    totalFundsCount: fundsById.size,
  };
};

/**
 * Conta o número de cotas únicas baseado na combinação de fundo/class/series
 * Uma cota é considerada única se a combinação (fundo + class + series) for única
 *
 * @param walletFunds - Array de fundos da carteira
 * @returns Número de cotas únicas
 */
export const countUniqueQuotas = (
  walletFunds: WalletFund[] | undefined,
): number => {
  if (!walletFunds || walletFunds.length === 0) {
    return 0;
  }

  const uniqueQuotaCombinations = new Set<string>();

  walletFunds.forEach((walletFund) => {
    const { operation, quotas } = walletFund;

    if (!quotas || quotas.length === 0) {
      return;
    }

    quotas.forEach((quota) => {
      const uniqueKey = `${operation.hashId}_${quota.class}_${quota.series}`;
      uniqueQuotaCombinations.add(uniqueKey);
    });
  });

  return uniqueQuotaCombinations.size;
};

/**
 * Obtém as cotas únicas com detalhes baseado na combinação de fundo/class/series
 *
 * @param walletFunds - Array de fundos da carteira
 * @returns Array de cotas únicas com informações do fundo
 */
export const getUniqueQuotas = (
  walletFunds: WalletFund[],
): Array<{
  fundId: string;
  fundName: string;
  class: string;
  series: number;
  totalAmount: number;
}> => {
  if (!walletFunds || walletFunds.length === 0) {
    return [];
  }

  const uniqueQuotasMap = new Map<
    string,
    {
      fundId: string;
      fundName: string;
      class: string;
      series: number;
      totalAmount: number;
    }
  >();

  walletFunds.forEach((walletFund) => {
    const { operation, quotas } = walletFund;

    // Se não há quotas, pula este fund
    if (!quotas || quotas.length === 0) {
      return;
    }

    quotas.forEach((quota) => {
      const uniqueKey = `${operation.hashId}_${quota.class}_${quota.series}`;

      if (uniqueQuotasMap.has(uniqueKey)) {
        const existing = uniqueQuotasMap.get(uniqueKey);
        if (existing) {
          existing.totalAmount += quota.amount;
        }
      } else {
        uniqueQuotasMap.set(uniqueKey, {
          fundId: operation.hashId,
          fundName: operation.name,
          class: quota.class,
          series: quota.series,
          totalAmount: quota.amount,
        });
      }
    });
  });

  return Array.from(uniqueQuotasMap.values());
};

/**
 * Conta o número total de cotas (incluindo duplicatas) em uma carteira
 *
 * @param walletFunds - Array de fundos da carteira
 * @returns Número total de cotas
 */
export const countTotalQuotas = (walletFunds: WalletFund[]): number => {
  if (!walletFunds || walletFunds.length === 0) {
    return 0;
  }

  return walletFunds.reduce((total, walletFund) => {
    return total + (walletFund.quotas?.length || 0);
  }, 0);
};

/**
 * Obtém a somatória das cotas únicas para um array de wallets
 * Segue a mesma premissa de contagem única porém por
 * carteira/fundo/class/series acrescentando a wallet
 *
 * @param wallets - Array de wallets
 * @returns Objeto com total de cotas únicas e detalhes por wallet
 */
export const getWalletsUniqueQuotasSummary = (
  wallets: Array<{ walletFunds: WalletFund[] }>,
): {
  totalUniqueQuotas: number;
  walletsSummary: Array<{
    walletIndex: number;
    uniqueQuotas: number;
    totalQuotas: number;
  }>;
} => {
  if (!wallets || wallets.length === 0) {
    return {
      totalUniqueQuotas: 0,
      walletsSummary: [],
    };
  }

  const globalUniqueQuotaCombinations = new Set<string>();

  const walletsSummary = wallets.map((wallet, walletIndex) => {
    const walletFunds = wallet.walletFunds || [];
    const uniqueQuotas = countUniqueQuotas(walletFunds);
    const totalQuotas = countTotalQuotas(walletFunds);

    walletFunds.forEach((walletFund) => {
      const { operation, quotas } = walletFund;

      if (quotas && quotas.length > 0) {
        quotas.forEach((quota) => {
          const uniqueKey = `${walletIndex}_${operation.hashId}_${quota.class}_${quota.series}`;
          globalUniqueQuotaCombinations.add(uniqueKey);
        });
      }
    });

    return {
      walletIndex,
      uniqueQuotas,
      totalQuotas,
    };
  });

  return {
    totalUniqueQuotas: globalUniqueQuotaCombinations.size,
    walletsSummary,
  };
};
